import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { storeFbCookies } from '../components/funnelV3/analytics/funnelV3Analytics';
import {
    configureMarketingTags,
    ensureFbqStub,
    onMarketingTagsLoaded,
    scheduleMarketingTags,
} from '../utils/marketingTags';

type StandardEvent =
    | 'PageView'
    | 'ViewContent'
    | 'Search'
    | 'AddToCart'
    | 'AddToWishlist'
    | 'InitiateCheckout'
    | 'AddPaymentInfo'
    | 'Purchase'
    | 'Lead'
    | 'CompleteRegistration';

interface EventData {
    value?: number;
    currency?: string;
    content_name?: string;
    content_ids?: string[];
    content_type?: string;
    [key: string]: any;
}

interface FacebookPixelContextType {
    trackEvent: (eventName: StandardEvent, data?: EventData) => void;
    trackCustomEvent: (eventName: string, data?: Record<string, any>) => void;
    updateAdvancedMatching: (userData: { em?: string; fn?: string; ln?: string }) => void;
    grantConsent: () => void;
    revokeConsent: () => void;
    hasConsent: boolean;
    isInitialized: boolean;
}

interface FacebookPixelProviderProps {
    children: ReactNode;
    pixelId: string;
    /** Optional second Meta pixel — both receive init, AM updates, and track calls. */
    pixelId2?: string;
    autoPageView?: boolean;
    requireConsent?: boolean;
    debug?: boolean;
}

const FacebookPixelContext = createContext<FacebookPixelContextType | undefined>(
    undefined
);

export const FacebookPixelProvider: React.FC<FacebookPixelProviderProps> = ({
    children,
    pixelId,
    pixelId2,
    autoPageView = true,
    requireConsent = false,
    debug = false,
}) => {
    const [hasConsent, setHasConsent] = useState<boolean>(!requireConsent);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const pixelIds = useMemo(
        () => [pixelId, pixelId2].filter((id): id is string => !!id),
        [pixelId, pixelId2]
    );

    useEffect(() => {
        if (requireConsent && typeof window !== 'undefined') {
            const savedConsent = localStorage.getItem('fb_pixel_consent');
            if (savedConsent === 'granted') {
                setHasConsent(true);
            }
        }
    }, [requireConsent]);

    useEffect(() => {
        if (typeof window === 'undefined' || !hasConsent || pixelIds.length === 0) {
            return;
        }

        ensureFbqStub();
        configureMarketingTags({
            fbPixelIds: pixelIds,
            fbAutoPageView: autoPageView,
        });
        const cancelSchedule = scheduleMarketingTags();
        const unsubscribe = onMarketingTagsLoaded(() => {
            storeFbCookies();
            setIsInitialized(true);
            if (debug) {
                console.log(`[Facebook Pixel] Initialized with IDs: ${pixelIds.join(', ')}`);
            }
        });

        return () => {
            cancelSchedule();
            unsubscribe();
        };
    }, [pixelIds, hasConsent, autoPageView, debug]);

    const grantConsent = (): void => {
        setHasConsent(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'granted');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent granted');
        }
    };

    const revokeConsent = (): void => {
        setHasConsent(false);
        setIsInitialized(false);
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'revoked');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent revoked');
        }
    };

    const trackEvent = (eventName: StandardEvent, data: EventData = {}): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        ensureFbqStub();
        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Event tracked: ${eventName}`, data);
            }
        } else {
            console.warn('[Facebook Pixel] Not initialized');
        }
    };

    const trackCustomEvent = (
        eventName: string,
        data: Record<string, any> = {}
    ): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        ensureFbqStub();
        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('trackCustom', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Custom event tracked: ${eventName}`, data);
            }
        } else {
            console.warn('[Facebook Pixel] Not initialized');
        }
    };

    /**
     * Update Meta Pixel Advanced Matching after email/name capture.
     * Re-inits all pixels with { em, fn, ln } so subsequent fbq('track', ...)
     * calls in this session include AM.
     */
    const updateAdvancedMatching = useCallback((userData: { em?: string; fn?: string; ln?: string }) => {
        if (typeof window === 'undefined') return;
        ensureFbqStub();
        if (!window.fbq) return;
        const cleaned: Record<string, string> = {};
        if (userData.em) cleaned.em = userData.em.toLowerCase().trim();
        if (userData.fn) cleaned.fn = userData.fn.toLowerCase().trim();
        if (userData.ln) cleaned.ln = userData.ln.toLowerCase().trim();
        if (Object.keys(cleaned).length === 0) return;
        for (const id of pixelIds) {
            window.fbq('init', id, cleaned);
        }
    }, [pixelIds]);

    return (
        <FacebookPixelContext.Provider
            value={{
                trackEvent,
                trackCustomEvent,
                updateAdvancedMatching,
                grantConsent,
                revokeConsent,
                hasConsent,
                isInitialized,
            }}
        >
            {children}
        </FacebookPixelContext.Provider>
    );
};

export const useFacebookPixel = (): FacebookPixelContextType => {
    const context = useContext(FacebookPixelContext);
    if (!context) {
        throw new Error(
            'useFacebookPixel must be used within FacebookPixelProvider'
        );
    }
    return context;
};

// Cookie consent banner component
export const CookieConsentBanner: React.FC = () => {
    const { hasConsent, grantConsent } = useFacebookPixel();
    const [visible, setVisible] = useState<boolean>(!hasConsent);

    if (!visible || hasConsent) {
        return null;
    }

    const handleAccept = (): void => {
        grantConsent();
        setVisible(false);
    };

    const handleDecline = (): void => {
        setVisible(false);
    };

    const bannerStyle: React.CSSProperties = {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 9999,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    };

    const buttonStyle: React.CSSProperties = {
        padding: '10px 20px',
        marginLeft: '10px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    };

    return (
        <div style={bannerStyle}>
            <p style={{ margin: 0, flex: 1 }}>
                We use cookies and tracking pixels to improve your experience and analyze site
                traffic. By clicking "Accept", you consent to our use of cookies.
            </p>
            <div>
                <button
                    onClick={handleDecline}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#95a5a6',
                        color: 'white',
                    }}
                >
                    Decline
                </button>
                <button
                    onClick={handleAccept}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#27ae60',
                        color: 'white',
                    }}
                >
                    Accept
                </button>
            </div>
        </div>
    );
};

export type { StandardEvent, EventData, FacebookPixelContextType };